import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BimApi } from "api/bim.api";
import { ConversionApi } from "api/conversion.api";
import { FileInfo, FileInfoResponse, ILinksInformation, PayloadOriginFile } from "common/define";
import { RootEpic } from "common/type-state";
import { merge, Observable } from "rxjs";
import { catchError, concatMap, distinctUntilChanged, filter, mergeMap, switchMap, withLatestFrom } from "rxjs/operators";
import Utils from "utils/utils";
import { setAppLoading } from "./app.slice";
import { FileListHelper } from './helper';
import { closeViewerObser } from "./helper/multiViewer.helper";
import { closeViewerComplete, firstFileDisplay } from "./multiViewer.slice";

const initState: FileListHelper.FilesListState = {
    allCaches: [],
    filesList: [],
    linkFilesList: [],
    filesOrigin: [],
    loading: false,
    filesListThumnail: [],
    refreshFileList: ''
};

const filesListSlice = createSlice({
    name: 'filesList',
    initialState: initState,
    reducers: {
        getCacheFilesList: (state, action: PayloadAction<PayloadOriginFile[]>) => {
            state.loading = true;
        },
        deleteFile(state, action: PayloadAction<ViewId>) {
            return
        },
        updateAllCaches(state, action: PayloadAction<FileInfoResponse[]>) {
            const allCaches = action.payload;
            allCaches && (state.allCaches = allCaches);
            state.loading = false
        },
        updateFileListComplete(state, action: PayloadAction<FileListHelper.ResultFileListState>) {
            const { newFileList, newFileThumnail, newFilesOrigin } = action.payload;
            const linkFilesList: FileInfo[] = [];                
            if (newFileList) {
                state.filesList = newFileList;
                newFileList.forEach(f => {
                    if (f.listFile && f.listFile.length > 0) {
                        linkFilesList.push(...f.listFile);                        
                    }
                });
            };
            newFileThumnail && (state.filesListThumnail = newFileThumnail);
            newFilesOrigin && (state.filesOrigin = newFilesOrigin);
            linkFilesList && (state.linkFilesList = linkFilesList);
            state.loading = false
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload
        },
        errorFilesList: (state) => {
            state.loading = false;
        },
        uploadThumnail: (state, action: PayloadAction<{ file: FileInfo, thumnailSrc: string}>) => { return },
        refreshFilesList: (state, action: PayloadAction<undefined>) => {
            state.refreshFileList = Communicator.UUID.create();
        },
    }
});

/**
 * Effect get cache file list + transform file item
 * @param action$
 * @param state$
 * @returns
 */
const filesList$: RootEpic = (action$, state$) => action$.pipe(
    filter(getCacheFilesList.match),
    distinctUntilChanged(),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
        const list = action.payload;
        const preList = state.filesList.filesOrigin;
        const allCaches = state.filesList.allCaches;
        const arrDiff = FileListHelper.filterListAdd(list, preList);
        if (arrDiff) {
            return ConversionApi.getCacheByListFile(arrDiff).pipe(
                concatMap(
                    (result) => new Observable<FileInfoResponse[]>(subscriber => {
                        const newResult: FileInfoResponse[] = [];
                        result.forEach(async x => {
                            if (x.modelFileId == null || x.modelFileId === '') { // 1 số tình huống file lớn sẽ bị miss infor này măcc dù cache đã có
                                if (x.streamLocation) {
                                    x.fileLocation = `${x.streamLocation}/${x.filename.split('.')[0]}.assemblytree.xml`;
                                    x.modelFileId = await BimApi.getModelFileId(x).toPromise();
                                    if (x.modelFileId == null || x.modelFileId === "")
                                        x.modelFileId = await BimApi.createModelFileId(x).toPromise();
                                    if (x.modelFileId != null && x.modelFileId !== "")
                                        ConversionApi.updateModelId({
                                            fileName: x.filename,
                                            baseFileId: x.baseFileId,
                                            baseMinorRev: x.baseMinorRev ?? 0,
                                            baseMajorRev: x.baseMajorRev ?? 0,
                                            modelFileId: x.modelFileId,

                                        }).subscribe();
                                }
                            }
                            if (x.extraConvertOutput) x.extraConvert = JSON.parse(x.extraConvertOutput);

                            const list: FileInfoResponse[] = []; 
                            if (x.extraConvert && x.extraConvert.ManageLinks && x.originalFilePath) {
                                x.extraConvert.ManageLinks.forEach((lf: ILinksInformation) => {
                                    let linkName = lf.SavedPath;
                                    if (linkName) {
                                        linkName = linkName.toLowerCase().trim();
                                        if (Utils.isLinkFile(lf.SavedPath)) {
                                            const index = allCaches.findIndex(v => linkName === v.filename?.toLowerCase()?.trim() && x.originalFilePath === v.originalFilePath);
                                            if (!lf.LinkId) lf.LinkId = `id-${index}`;
                                            if (index >= 0) {
                                                const f = allCaches[index];
                                                list.push({
                                                    ...f,
                                                    linksInformation: lf
                                                })
                                            }
                                        }
                                    }
                                });                     
                                
                                if (list?.length > 0) {
                                    x.linkFiles = list;
                                }
                            }
                            newResult.push(x);
                            if (newResult.length === result.length) {
                                subscriber.next(newResult);
                                subscriber.complete();
                            }
                        });
                    })
                ),
                concatMap(
                    (newResult) => FileListHelper.addFilesList$(newResult, state.filesList)
                ),
                mergeMap(
                    resultAdd => {
                        if (preList.length > 0) {
                            return [
                                filesListSlice.actions.updateFileListComplete(resultAdd)
                            ]
                        } else {
                            return [
                                filesListSlice.actions.updateFileListComplete(resultAdd),
                                firstFileDisplay()
                            ]
                        }
                    }
                ),
                catchError(err => [errorFilesList()])
            )
        }
        return [filesListSlice.actions.setLoading(false)]
    })
)
const deleteFile$: RootEpic = (action$, state$) => action$.pipe(
    filter(deleteFile.match),
    withLatestFrom(state$),
    concatMap(([{ payload: viewId }, state]) => {
        return merge(
            [setAppLoading(true)],
            closeViewerObser(viewId, state.multiViewer).pipe(
                mergeMap(resultClose => {
                    return merge(
                        [closeViewerComplete(resultClose)],
                        FileListHelper.deleteFileObser(viewId, state.filesList).pipe(
                            mergeMap(resultDelete => [
                                setAppLoading(false),
                                filesListSlice.actions.updateFileListComplete(resultDelete)
                            ]),
                            catchError(err => [setAppLoading(false)])
                        )
                    )
                })
            )
        )
    }),

)
const uploadThumnail$: RootEpic = (action$, state$) => action$.pipe(
    filter(uploadThumnail.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
        const { file, thumnailSrc } = action.payload;
        return ConversionApi.uploadThumnail(file, thumnailSrc).pipe(
            mergeMap(res => {
                console.log(res);
                return[]
            }),
            catchError(err => {
                console.log(err);
                return []
            })
        )
    })
)

export const FilesListEpics = [
    filesList$,
    deleteFile$,
    uploadThumnail$
];

export const {
    updateAllCaches,
    getCacheFilesList,
    errorFilesList,
    deleteFile,
    uploadThumnail,
    refreshFilesList
} = filesListSlice.actions;
export default filesListSlice.reducer;
